/**
 * SecuritySettingsTab - Admin Security Settings
 *
 * Manages security settings including two-factor authentication by email.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SecuritySettings } from '@budgira/shared';
import { Button, Card } from '../../components/ui';
import api from '../../services/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export function SecuritySettingsTab() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [maxLoginAttempts, setMaxLoginAttempts] = useState(5);
  const [max2FAAttempts, setMax2FAAttempts] = useState(3);
  const [lockoutDurationMinutes, setLockoutDurationMinutes] = useState(15);
  const [twoFACodeValidityMinutes, setTwoFACodeValidityMinutes] = useState(5);
  const [emailVerificationExpiryHours, setEmailVerificationExpiryHours] = useState(24);
  const [accountDeletionDelayDays, setAccountDeletionDelayDays] = useState(30);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Load settings
  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiResponse<SecuritySettings>>('/admin/security-settings');
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setSettings(data);
        setTwoFactorRequired(data.twoFactorRequired);
        setMaxLoginAttempts(data.maxLoginAttempts);
        setMax2FAAttempts(data.max2FAAttempts);
        setLockoutDurationMinutes(data.lockoutDurationMinutes);
        setTwoFACodeValidityMinutes(data.twoFACodeValidityMinutes);
        setEmailVerificationExpiryHours(data.emailVerificationExpiryHours ?? 24);
        setAccountDeletionDelayDays(data.accountDeletionDelayDays ?? 30);
        setRegistrationEnabled(data.registrationEnabled ?? true);
        setMaintenanceMode(data.maintenanceMode ?? false);
      } else {
        setError(response.data.error?.message || t('admin.security.loadError'));
      }
    } catch {
      setError(t('admin.security.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save settings
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.put<ApiResponse<SecuritySettings>>('/admin/security-settings', {
        twoFactorRequired,
        maxLoginAttempts,
        max2FAAttempts,
        lockoutDurationMinutes,
        twoFACodeValidityMinutes,
        emailVerificationExpiryHours,
        accountDeletionDelayDays,
        registrationEnabled,
        maintenanceMode,
      });

      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        setSuccessMessage(t('admin.security.saveSuccess'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.data.error?.message || t('admin.security.saveError'));
      }
    } catch {
      setError(t('admin.security.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Check if form has changes
  const hasChanges = settings && (
    twoFactorRequired !== settings.twoFactorRequired ||
    maxLoginAttempts !== settings.maxLoginAttempts ||
    max2FAAttempts !== settings.max2FAAttempts ||
    lockoutDurationMinutes !== settings.lockoutDurationMinutes ||
    twoFACodeValidityMinutes !== settings.twoFACodeValidityMinutes ||
    emailVerificationExpiryHours !== (settings.emailVerificationExpiryHours ?? 24) ||
    accountDeletionDelayDays !== (settings.accountDeletionDelayDays ?? 30) ||
    registrationEnabled !== (settings.registrationEnabled ?? true) ||
    maintenanceMode !== (settings.maintenanceMode ?? false)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {successMessage}
        </div>
      )}

      {/* Access Control Section */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center">
              <LockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('admin.security.accessControl.title')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('admin.security.accessControl.description')}
              </p>
            </div>
          </div>

          <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-4">
            {/* Registration Enabled Toggle */}
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.accessControl.registrationEnabled')}
                </span>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.accessControl.registrationEnabledDescription')}
                </p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={registrationEnabled}
                  onChange={(e) => setRegistrationEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 dark:peer-focus:ring-violet-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </div>
            </label>

            {/* Maintenance Mode Toggle */}
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.accessControl.maintenanceMode')}
                </span>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.accessControl.maintenanceModeDescription')}
                </p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={maintenanceMode}
                  onChange={(e) => setMaintenanceMode(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 dark:peer-focus:ring-violet-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
              </div>
            </label>

            {/* Warning when maintenance mode is enabled */}
            {maintenanceMode && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <WarningIcon className="w-4 h-4 flex-shrink-0" />
                  {t('admin.security.accessControl.maintenanceModeWarning')}
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Two-Factor Authentication Section */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/40 rounded-lg flex items-center justify-center">
              <ShieldIcon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('admin.security.twoFactor.title')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('admin.security.twoFactor.description')}
              </p>
            </div>
          </div>

          {/* Enable 2FA Toggle */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.twoFactor.enable')}
                </span>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.twoFactor.enableDescription')}
                </p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={twoFactorRequired}
                  onChange={(e) => setTwoFactorRequired(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 dark:peer-focus:ring-violet-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </div>
            </label>
          </div>
        </div>
      </Card>

      {/* Security Parameters */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
              <CogIcon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('admin.security.parameters.title')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('admin.security.parameters.description')}
              </p>
            </div>
          </div>

          <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-4">
            {/* Max Login Attempts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.parameters.maxLoginAttempts')}
                </label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.parameters.maxLoginAttemptsDescription')}
                </p>
              </div>
              <input
                type="number"
                min={1}
                max={10}
                value={maxLoginAttempts}
                onChange={(e) => setMaxLoginAttempts(parseInt(e.target.value) || 5)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Max 2FA Attempts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.parameters.max2FAAttempts')}
                </label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.parameters.max2FAAttemptsDescription')}
                </p>
              </div>
              <input
                type="number"
                min={1}
                max={10}
                value={max2FAAttempts}
                onChange={(e) => setMax2FAAttempts(parseInt(e.target.value) || 3)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Lockout Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.parameters.lockoutDuration')}
                </label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.parameters.lockoutDurationDescription')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={lockoutDurationMinutes}
                  onChange={(e) => setLockoutDurationMinutes(parseInt(e.target.value) || 15)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {t('admin.security.parameters.minutes')}
                </span>
              </div>
            </div>

            {/* 2FA Code Validity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.parameters.codeValidity')}
                </label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.parameters.codeValidityDescription')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={twoFACodeValidityMinutes}
                  onChange={(e) => setTwoFACodeValidityMinutes(parseInt(e.target.value) || 5)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {t('admin.security.parameters.minutes')}
                </span>
              </div>
            </div>

            {/* Email Verification Expiry */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.parameters.emailVerificationExpiry', 'Expiration du lien de vérification')}
                </label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.parameters.emailVerificationExpiryDescription', 'Durée de validité du lien de vérification d\'email envoyé aux nouveaux utilisateurs.')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={emailVerificationExpiryHours}
                  onChange={(e) => setEmailVerificationExpiryHours(parseInt(e.target.value) || 24)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {t('admin.security.parameters.hours', 'heures')}
                </span>
              </div>
            </div>

            {/* Account Deletion Delay */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="font-medium text-slate-900 dark:text-slate-100">
                  {t('admin.security.parameters.accountDeletionDelay', 'Délai de suppression de compte')}
                </label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('admin.security.parameters.accountDeletionDelayDescription', 'Nombre de jours avant la suppression définitive d\'un compte après demande de l\'utilisateur (RGPD).')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={7}
                  max={90}
                  value={accountDeletionDelayDays}
                  onChange={(e) => setAccountDeletionDelayDays(parseInt(e.target.value) || 30)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {t('admin.security.parameters.days', 'jours')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          isLoading={isSaving}
          disabled={!hasChanges || isSaving}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

// Icon components
function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

export default SecuritySettingsTab;
